import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    OnInit,
    output,
    signal,
    ViewChild,
} from '@angular/core';
import { OverlayDirective, OverlayService } from '@dxp/components';
import { TelXLPanelComponent, TelXLTabComponent } from '@dxp/elements';
import { EndCustomerComponent } from '@dxp/end-customer';
import { recalculateHeight } from '@dxp/core/helpers';
import { ICustomerDetails, ITab, IWorkItem } from '@dxp/core/models';
import {
    ContactService,
    PermissionService,
    SettingsService,
} from '@dxp/core/services';

type Options = 'customer-details';

@Component({
    selector: 'customer-interactions',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TelXLPanelComponent,
        TelXLTabComponent,
        EndCustomerComponent,
        OverlayDirective,
    ],
    providers: [OverlayService],
    templateUrl: './customer-interactions.component.html',
    styleUrl: './customer-interactions.component.scss',
})
export class CustomerInteractionsComponent implements OnInit {
    readonly #settingsService = inject(SettingsService);
    readonly #overlayService = inject(OverlayService);
    readonly #permissionService = inject(PermissionService);
    readonly #contactService = inject(ContactService);

    readonly #selected = signal<Options>('customer-details');

    autoRefreshOnUpdate = input<boolean>(false);
    workitem = input<IWorkItem>();
    customer = input<string>();
    allowNavigation = input<boolean>(true);
    updated = output<ICustomerDetails>();

    readonly tabs: ITab[] = [
        { label: 'Customer Details', index: 'customer-details' },
    ];

    @ViewChild(OverlayDirective, { static: true }) host!: OverlayDirective;

    ngOnInit() {
        this.#overlayService.registerHost(this.host);
    }

    height = computed<string>(() =>
        recalculateHeight('communication', this.#settingsService.viewType()),
    );

    onUpdated(customer: ICustomerDetails) {
        /**
         * Refreshes the parent directory list - When the form is updated
         */
        if (this.autoRefreshOnUpdate()) {
            this.#contactService.setContactUpdate({
                type: 'customers',
                contact: customer,
            });
        }

        this.updated.emit(customer);
    }

    get selected() {
        return this.#selected();
    }

    set selected(value: Options) {
        this.#selected.set(value);
    }

    setSelected(value: string) {
        this.selected = value as Options;
    }

    get hasPermission(): boolean {
        return this.#permissionService.hasAnyPermission(['customer.read']);
    }
}
